'use client';

import { atom, useAtomValue, useSetAtom } from 'jotai';
import { type PropsWithChildren, createElement, useEffect, useRef } from 'react';
import { useMotionValueEvent } from 'motion/react';
import { useProgressTracker } from './_lib/useProgressTracker';

export type Brands = 'core' | 'enterprise' | 'link';
export type Themes = 'dark' | 'light';

export const defaultBrand = 'core';
export const defaultTheme = 'dark';

const brandAtom = atom<Brands>(defaultBrand);
const themeAtom = atom<Themes>(defaultTheme);

export function useBrandValue() {
	const brand = useAtomValue(brandAtom);
	return brand;
}

export function useThemeValue() {
	const theme = useAtomValue(themeAtom);
	return theme;
}

export function useSetBrand() {
	const setBrand = useSetAtom(brandAtom);
	return (brand: Brands) => setBrand(brand);
}

export function useSetTheme() {
	const setTheme = useSetAtom(themeAtom);
	return (theme: Themes) => setTheme(theme);
}

const navThemeAtom = atom<Themes>(defaultTheme);

export function useNavThemeValue() {
	const theme = useAtomValue(navThemeAtom);
	return theme;
}

export function useSetNavTheme() {
	const set = useSetAtom(navThemeAtom);
	return (theme: Themes) => set(theme);
}

export type Themed = {
	className?: string;
	defaultPageBrand?: Brands;
	defaultPageTheme?: Themes;
	reversedTheme?: boolean;
	needHeaderThemeChange?: boolean;
};
export type HTMLElements =
	| 'article'
	| 'aside'
	| 'blockquote'
	| 'dialog'
	| 'div'
	| 'footer'
	| 'form'
	| 'header'
	| 'main'
	| 'menu'
	| 'nav'
	| 'ol'
	| 'section'
	| 'span'
	| 'table'
	| 'ul'
	| 'video';

export function Theme({
	as = 'div',
	className,
	defaultPageBrand,
	defaultPageTheme,
	reversedTheme = false,
	needHeaderThemeChange = false,
	children,
}: PropsWithChildren<
	{
		as?: HTMLElements;
	} & Themed
>) {
	let headerChanged = useRef<{ value: boolean }>({ value: false }).current.value;
	const ref = useRef<HTMLElement | null>(null);
	const setHeaderTheme = useSetNavTheme();
	const setTheme = useSetTheme();
	const currentBrand = useBrandValue();
	const currentTheme = useThemeValue();

	useEffect(() => {
		if (defaultPageTheme) {
			setTheme(defaultPageTheme);
			setHeaderTheme(defaultPageTheme);
		}
	}, [defaultPageTheme]);

	let brand = defaultPageBrand ?? currentBrand;
	let theme = defaultPageTheme ?? currentTheme;

	if (reversedTheme) {
		if (theme === 'light') {
			theme = 'dark';
		} else {
			theme = 'light';
		}
	}

	const Component = createElement(as, { 'data-brand': brand, 'data-theme': theme, className: className, ref: ref }, [children]);

	/**
	 * Header theme change when component is under it
	 */
	const progress = useProgressTracker(ref, { top: -0.95, bottom: -0.05 });

	useMotionValueEvent(progress, 'change', latest => {
		if (!needHeaderThemeChange) return;

		// change header theme
		if (latest >= 0.005 && latest <= 0.995 && !headerChanged) {
			setHeaderTheme(theme);
			headerChanged = true;
		}

		// restore header theme
		if ((latest <= -0.005 || latest >= 1.005) && headerChanged) {
			setHeaderTheme(currentTheme);
			headerChanged = false;
		}
	});

	return Component;
}
